import { useQuery } from "@tanstack/react-query";
import { bbox } from "@turf/bbox";
import bboxPolygon from "@turf/bbox-polygon";
import { buffer } from "@turf/buffer";
import { point } from "@turf/helpers";
import { useDebounce } from "@uidotdev/usehooks";
import { useAtom } from "jotai";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
    AerialImagesList,
    EmissionRecordsListRequest,
    Equipment,
} from "../../../../apiClient/generated";
import {
    useEmissionRecordsApiClient,
    useInfrastructureApiClient,
    useMapApiClient,
} from "../../../../hooks";
import { createFeatureCollection } from "../../../../utils/geopatialUtils";
import type { EmissionRecordMapWithBin } from "../../../MapV2/CustomTypes";
import { EMISSION_LAYER_STYLES } from "../../../MapV2/constants";
import { PlumeControl } from "../../../MapV2/elements/DataControlsV2/PlumeControl";
import { useEmissionRecordsOnMap } from "../../../MapV2/hooks/emissions";
import { useMapData } from "../../../MapV2/hooks/mapDataAndFilters";
import { useMap } from "../../../MapV2/hooks/mapState";
import {
    EquipmentLayer,
    SiteLayer,
} from "../../../MapV2/layers/infrastructure";
import { RasterLayer } from "../../../MapV2/layers/rasters";
import { MapBase } from "../../../MapV2/Map";
import { MapPopup, type PickedItems } from "../../../MapV2/MapPopup";
import {
    endOfUtcDay,
    hoverContextAtom,
    selectionContextAtom,
    startOfUtcDay,
} from "../state";

interface SiteMapProps {
    siteId: string;
    dateFrom: Date;
    dateTo: Date;
    filters?: EmissionRecordsListRequest;
}

export const SiteMap = (props: SiteMapProps) => {
    const infrastructureApiClient = useInfrastructureApiClient();
    const mapApiClient = useMapApiClient();
    const { flyTo } = useMap(`site-${props.siteId}`);
    const [pickedItems, setPickedItems] = useState<PickedItems>();
    const containerRef = useRef<HTMLDivElement>();

    // Selection context
    const [selectionContext, setSelectionContext] =
        useAtom(selectionContextAtom);

    // Hover context
    const [hoverContext, setHoverContext] = useAtom(hoverContextAtom);
    const [hoveringOnMap, setHoveringOnMap] = useState(false);

    // Selected emission data, if any
    const apiClient = useEmissionRecordsApiClient();
    const emissionQuery = useQuery({
        queryKey: ["emissionRecord", selectionContext.emissionId],
        queryFn: async () => {
            const response = await apiClient.emissionRecordsRetrieve({
                id: selectionContext.emissionId,
            });
            return response;
        },
        refetchOnWindowFocus: false,
        enabled: !!selectionContext.emissionId,
        staleTime: Infinity,
    });

    // Map settings
    const mapName = useMemo(() => `site-${props.siteId}`, [props.siteId]);
    const { selectedContext, setSelectedContext } = useMapData(mapName);

    // Sync date range from timeline
    const debouncedDates = useDebounce(selectionContext.dateRange, 50);
    useEffect(() => {
        if (!debouncedDates) {
            setSelectedContext((s) =>
                s.dateRangeToShowData === undefined
                    ? s
                    : { ...s, dateRangeToShowData: undefined },
            );
            return;
        }
        setSelectedContext((s) => ({
            ...s,
            dateRangeToShowData: {
                start: debouncedDates.start.toISOString(),
                end: debouncedDates.end.toISOString(),
            },
        }));
    }, [debouncedDates, setSelectedContext]);

    // Sync selected emission and plume
    useEffect(() => {
        if (selectionContext.emissionId && emissionQuery.data) {
            setSelectedContext((s) => ({
                ...s,
                emissionRecordId: emissionQuery.data.id,
                relatedPlume: emissionQuery.data.dataPoint.plumeImage,
            }));
        }
        if (!selectionContext.emissionId && selectedContext.emissionRecordId) {
            setSelectedContext((s) => ({
                ...s,
                emissionRecordId: undefined,
                relatedPlume: undefined,
                dateRangeToShowData: undefined,
            }));
        }
    }, [
        selectionContext.emissionId,
        setSelectedContext,
        selectedContext.emissionRecordId,
        emissionQuery.data,
    ]);

    // Sync hover context (row/timeline hover) into map filters
    useEffect(() => {
        setSelectedContext((s) => ({
            ...s,
            emissionRecordIds: hoverContext?.emissionIds,
            plumeIds: hoverContext?.relatedPlumeImages,
        }));
    }, [hoverContext, setSelectedContext]);

    // Site data
    const siteQuery = useQuery({
        queryKey: ["site", props.siteId],
        queryFn: async () => {
            const response =
                await infrastructureApiClient.infrastructureSitesRetrieve({
                    id: props.siteId,
                });
            return response;
        },
        refetchOnWindowFocus: false,
    });
    useEffect(() => {
        if (siteQuery.data?.location) {
            const timeout = setTimeout(() => {
                flyTo(
                    siteQuery.data.location.coordinates[1],
                    siteQuery.data.location.coordinates[0],
                    14,
                    true,
                );
            }, 400);
            return () => clearTimeout(timeout);
        }
    }, [siteQuery.data, flyTo]);
    // Aerial images
    const aerialImageQuery = useQuery({
        queryKey: [
            "siteAerialImages",
            props.siteId,
            props.dateFrom.toISOString(),
            props.dateTo.toISOString(),
        ],
        queryFn: async () => {
            const response = await mapApiClient.mapAerialImagesList({
                site: [props.siteId],
                takenAtDataRangeAfter: props.dateFrom,
                takenAtDataRangeBefore: props.dateTo,
            });
            return response;
        },
        refetchOnWindowFocus: false,
    });
    // Compute aerial image layers to display - only the latest image for the site.
    // The query covers the whole page range, so narrowing to the timeline
    // selection happens here and never triggers a refetch. `takenAt` is
    // day-precision (midnight UTC), so the selection is widened to whole UTC
    // days - comparing it against an intraday selection would match nothing.
    const aerialImageLayers = useMemo(() => {
        const rangeStart = startOfUtcDay(
            debouncedDates?.start ?? props.dateFrom,
        );
        const rangeEnd = endOfUtcDay(debouncedDates?.end ?? props.dateTo);
        const newestImage = (aerialImageQuery.data?.results || [])
            .filter(
                (aerialImage: AerialImagesList) =>
                    aerialImage.takenAt >= rangeStart &&
                    aerialImage.takenAt <= rangeEnd,
            )
            .reduce<AerialImagesList | undefined>(
                (newest, current) =>
                    !newest || current.takenAt > newest.takenAt
                        ? current
                        : newest,
                undefined,
            );

        return [
            RasterLayer({
                data: newestImage ? [newestImage] : [],
                name: "aerial_image",
                visible: true,
                zoomLevel: 20,
                opacity: 1,
            }),
        ];
    }, [aerialImageQuery.data, debouncedDates, props.dateFrom, props.dateTo]);

    // Equipment data
    const equipmentQuery = useQuery({
        queryKey: ["equipmentForSiteResults", props.siteId],
        queryFn: async () => {
            const response =
                await infrastructureApiClient.infrastructureEquipmentList({
                    site: [props.siteId],
                    pageSize: 200,
                });
            return response.results;
        },
        refetchOnWindowFocus: false,
    });

    // Nearby sites
    const sitesNearbyQuery = useQuery({
        queryKey: ["siteNearLocation", siteQuery.data?.location],
        queryFn: async () => {
            if (!siteQuery.data) {
                return [];
            }
            // Compute a 1km radius around point
            const bufferedArea = buffer(
                point(siteQuery.data.location.coordinates as [number, number]),
                1,
                { units: "kilometers" },
            );
            if (!bufferedArea) {
                return [];
            }
            // Retrieve sites
            const response =
                await infrastructureApiClient.infrastructureSitesList({
                    locationWithin: JSON.stringify(
                        bboxPolygon(bbox(bufferedArea)).geometry,
                    ),
                    pageSize: 30,
                });
            return response.results;
        },
        enabled: !!siteQuery.data,
        refetchOnWindowFocus: false,
    });

    // Emission data
    const thirdPartyEmissions = useEmissionRecordsOnMap(
        mapName,
        true,
        `${mapName}-third-party`,
        false,
        useMemo(
            () => ({
                ...props.filters,
                site: undefined,
                detectionDateRangeAfter: props.dateFrom,
                detectionDateRangeBefore: props.dateTo,
                dataSource: ["THIRD_PARTY"],
                relatedSite: props.siteId,
                providerWithSource: props.filters.providerWithSource ?? "all",
            }),
            [props.dateFrom, props.dateTo, props.siteId, props.filters],
        ),
        EMISSION_LAYER_STYLES.thirdParty,
        undefined,
        !hoveringOnMap,
    );

    const selfReportedEmissions = useEmissionRecordsOnMap(
        mapName,
        true,
        `${mapName}-self-reported`,
        false,
        useMemo(
            () => ({
                ...props.filters,
                site: undefined,
                detectionDateRangeAfter: props.dateFrom,
                detectionDateRangeBefore: props.dateTo,
                dataSource: ["SELF_REPORTED"],
                relatedSite: props.siteId,
                providerWithSource: props.filters.providerWithSource ?? "all",
            }),
            [props.dateFrom, props.dateTo, props.siteId, props.filters],
        ),
        EMISSION_LAYER_STYLES.operatorProvided,
        undefined,
        !hoveringOnMap,
    );

    // Map layers
    const layers = [
        ...aerialImageLayers,
        ...selfReportedEmissions.plumeLayers,
        ...thirdPartyEmissions.plumeLayers,
        SiteLayer(
            createFeatureCollection(
                sitesNearbyQuery.data ? sitesNearbyQuery.data : [],
            ).features,
            true,
            16,
            undefined,
            undefined,
            0.1,
            "nearbySites",
        ),
        SiteLayer(
            createFeatureCollection(siteQuery.data ? [siteQuery.data] : [])
                .features,
            true,
            16,
        ),
        EquipmentLayer(
            createFeatureCollection(
                equipmentQuery.data ? equipmentQuery.data : [],
            ).features,
            true,
            16,
        ),
        ...selfReportedEmissions.emissionLayers,
        ...thirdPartyEmissions.emissionLayers,
    ];

    return (
        <div
            ref={containerRef}
            className="relative w-full rounded overflow-hidden h-full"
            onMouseEnter={() => setHoveringOnMap(true)}
            onMouseLeave={() => setHoveringOnMap(false)}
        >
            <MapBase
                layers={layers}
                mapId={`site-${props.siteId}`}
                showBasemapControl
                showMeasureControl
                showScaleControl
                showZoomControl
                onLeftClick={({ info, event }) => {
                    const emissions = info.filter(
                        (i) => i.object && i.layer.id.includes("emission-"),
                    );
                    if (emissions.length === 0) {
                        setPickedItems(undefined);
                    } else if (emissions.length === 1) {
                        const emission = emissions[0].object
                            .properties as EmissionRecordMapWithBin;
                        const emissionTimestamp = DateTime.fromJSDate(
                            emission.detectionTimestamp,
                        );
                        setSelectionContext((s) => ({
                            ...s,
                            emissionId: emissions[0].object.properties.id,
                            dateRange: {
                                start: emissionTimestamp
                                    .minus({ days: 7 })
                                    .toJSDate(),
                                end: emissionTimestamp
                                    .plus({ days: 7 })
                                    .toJSDate(),
                            },
                            highlightPosition: {
                                date: emissionTimestamp.toJSDate(),
                                x: 0,
                            },
                        }));
                    } else {
                        const containerRect =
                            containerRef.current.getBoundingClientRect();
                        setPickedItems({
                            pointClicked: {
                                x: event.clientX - containerRect.left,
                                y: event.clientY - containerRect.top,
                            },
                            sites: [],
                            siteClusters: [],
                            equipment: [],
                            pipelines: [],
                            emissions: emissions,
                            emissionClusters: [],
                            dataPoints: [],
                            unmatchedDataPoints: [],
                            extraData: [],
                        });
                    }
                }}
                onHover={(info) => {
                    if (!info.object || !info.layer?.id.includes("emission-")) {
                        // Only clear if there's actually something to clear.
                        if (hoverContext !== undefined) {
                            setHoverContext(undefined);
                        }
                        return;
                    }

                    // Set hover context (only if it changed) - map can only highlight one at a time
                    const emissionData = info.object
                        .properties as EmissionRecordMapWithBin;
                    if (hoverContext?.emissionIds?.[0] !== emissionData.id) {
                        setHoverContext({
                            emissionIds: [emissionData.id],
                            relatedPlumeImages: [emissionData.plumeImage],
                        });
                    }
                }}
                getTooltip={(info) => {
                    if (!info.object?.properties) {
                        return;
                    }
                    if ("equipmentType" in info.object.properties) {
                        const equipment: Equipment = info.object.properties;
                        return `${equipment.equipmentClass || equipment.groupName} ${equipment.operatorUniqueId ? "(" + equipment.operatorUniqueId + ")" : ""}`;
                    }
                    if ("detectedRate" in info.object.properties) {
                        return `${info.object.properties.detectedRate / 1000} kg/h`;
                    }
                    if ("siteName" in info.object.properties) {
                        return info.object.properties.siteName;
                    }
                }}
            />

            {pickedItems && (
                <MapPopup
                    picked={pickedItems}
                    onClickItem={(item) => {
                        setPickedItems(undefined);
                        const emissionTimestamp = DateTime.fromJSDate(
                            item.object.properties.detectionTimestamp,
                        );
                        setSelectionContext((s) => ({
                            ...s,
                            emissionId: item.object.properties.id,
                            dateRange: {
                                start: emissionTimestamp
                                    .minus({ days: 7 })
                                    .toJSDate(),
                                end: emissionTimestamp
                                    .plus({ days: 7 })
                                    .toJSDate(),
                            },
                            highlightPosition: {
                                date: emissionTimestamp.toJSDate(),
                                x: 0,
                            },
                        }));
                    }}
                />
            )}

            <div className="absolute top-2 w-full flex justify-center">
                <PlumeControl mapId={mapName} variant="sm" />
            </div>
        </div>
    );
};
